import React, { useState } from 'react';
import Seo from '@/components/Seo';
import { fetchPartnerStats } from '@/lib/analytics';
import { IMAGES } from '@/lib/site';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Clock3,
  FileText,
  Gauge,
  Handshake,
  Building2,
  LockKeyhole,
  Mail,
  MapPin,
  MapPinned,
  Phone,
  Send,
  ShieldCheck,
  Target,
  TrendingUp,
  Users,
  Search,
  Megaphone,
  Layers3,
  CalendarDays,
} from 'lucide-react';

const BENEFITS = [
  {
    icon: Target,
    title: 'Local, high-intent enquiries',
    text: 'The website is focused specifically on homeowners searching for water-damage help in Coquitlam, Port Coquitlam and Port Moody.',
  },
  {
    icon: Phone,
    title: 'Calls + form leads',
    text: 'The lead system is built around two clear actions: calling for urgent help or submitting a short request form.',
  },
  {
    icon: Gauge,
    title: 'Measurable performance',
    text: 'Website activity, form submissions and call intent can be monitored so the partnership is based on real performance data.',
  },
  {
    icon: FileText,
    title: 'Simple lead delivery',
    text: 'Form enquiries can be routed with the customer name, phone, email when supplied, city, service need and problem description.',
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Homeowner requests help',
    text: 'A homeowner reaches the site through search or direct traffic and submits a request or calls the dedicated number.',
  },
  {
    n: '02',
    title: 'Lead is captured and tracked',
    text: 'Form enquiries are logged and call activity is measured so performance can be reviewed over time.',
  },
  {
    n: '03',
    title: 'Qualified local partner receives it',
    text: 'Once partnered, enquiries can be directed to the selected restoration provider serving the Tri-Cities.',
  },
];

const COVERAGE = [
  { city: 'Coquitlam', note: 'Town Centre, Burke Mountain, Maillardville, Austin Heights and surrounding neighbourhoods.' },
  { city: 'Port Coquitlam', note: 'Citadel, Mary Hill, Riverwood, Glenwood, Oxford Heights and surrounding neighbourhoods.' },
  { city: 'Port Moody', note: 'Suter Brook, Newport Village, Heritage Mountain, Moody Centre and surrounding neighbourhoods.' },
];

const PARTNER_PROFILE = [
  'Provides professional water-damage restoration services in the Tri-Cities.',
  'Can respond promptly to urgent homeowner enquiries.',
  'Communicates clearly with homeowners and follows through on leads.',
  'Is properly insured and operates in accordance with applicable local requirements.',
  'Is comfortable with transparent lead and performance reporting.',
];


const GROWTH_OPTIONS = [
  {
    icon: Search,
    eyebrow: 'Organic partnership',
    title: 'Lead Generation',
    text: 'A focused local lead-generation partnership built around organic search visibility, lead capture and transparent reporting.',
    features: [
      'Local SEO and ongoing optimization',
      'Form and phone lead routing',
      'Private performance dashboard',
      'Website maintenance',
      'No pay-per-lead fees',
      'Cancel anytime',
    ],
  },
  {
    icon: TrendingUp,
    eyebrow: 'Paid search growth',
    title: 'Google Ads Growth',
    text: 'Add professionally managed high-intent Google Search campaigns when you want to accelerate qualified local demand.',
    features: [
      'Everything in Lead Generation',
      'Google Ads campaign setup',
      'Keyword and competitor research',
      'Conversion tracking',
      'Campaign optimization',
      'Landing-page optimization',
      'Advertising budget paid separately',
    ],
  },
  {
    icon: Megaphone,
    eyebrow: 'Cross-channel growth',
    title: 'Google + Meta Growth',
    text: 'Combine search intent with Facebook and Instagram campaigns, retargeting and coordinated performance reporting.',
    features: [
      'Everything in Google Ads Growth',
      'Facebook and Instagram campaigns',
      'Retargeting audiences',
      'Creative and campaign optimization',
      'Cross-channel reporting',
      'Advertising budget paid separately',
    ],
  },
];

function StatCard({ icon: Icon, label, value, sub, compactValue = false }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-6 text-center shadow-sm">
      <div className="flex items-center justify-center gap-3 text-muted-foreground">
        <Icon className="h-5 w-5 text-water-strong" />
        <span className="text-sm font-semibold">{label}</span>
      </div>
      <div className={`mt-3 font-display font-extrabold text-primary ${compactValue ? 'text-2xl' : 'text-4xl'}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function PartnerPage() {
  const [key, setKey] = useState(() => sessionStorage.getItem('partnerKey') || '');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('month');
  const [partnerForm, setPartnerForm] = useState({ name: '', company: '', phone: '', email: '', interest: '', message: '', website: '' });
  const [partnerSubmitting, setPartnerSubmitting] = useState(false);
  const [partnerSuccess, setPartnerSuccess] = useState(false);
  const [partnerError, setPartnerError] = useState('');

  const updatePartnerForm = (field) => (e) => {
    setPartnerForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (partnerSuccess) setPartnerSuccess(false);
    if (partnerError) setPartnerError('');
  };

  const submitPartnerEnquiry = async (e) => {
    e.preventDefault();
    setPartnerSubmitting(true);
    setPartnerSuccess(false);
    setPartnerError('');

    try {
      const response = await fetch('/api/partner-contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(partnerForm),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to send your enquiry.');

      setPartnerSuccess(true);
      setPartnerForm({ name: '', company: '', phone: '', email: '', interest: '', message: '', website: '' });
    } catch (err) {
      setPartnerError(err.message || 'Unable to send your enquiry. Please try again.');
    } finally {
      setPartnerSubmitting(false);
    }
  };

  const loadStats = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await fetchPartnerStats(key);
      sessionStorage.setItem('partnerKey', key);
      setStats(data);
    } catch (err) {
      setStats(null);
      setError(err.message || 'Unable to load stats');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Restoration Partner Opportunity — Tri-Cities Water Restoration"
        description="Private restoration partner information for Tri-Cities Water Restoration."
        robots="noindex,nofollow,noarchive"
      />

      <section className="relative overflow-hidden bg-navy py-20 text-white lg:py-24">
        <img src={IMAGES.equipment} alt="Water damage restoration partner opportunity" className="absolute inset-0 h-full w-full object-cover opacity-50" loading="eager" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/75 to-navy/25" />
        <div className="pointer-events-none absolute inset-0 opacity-40">
          <div className="absolute -right-24 -top-32 h-96 w-96 rounded-full bg-water/20 blur-3xl" />
          <div className="absolute -bottom-40 left-1/4 h-96 w-96 rounded-full bg-water-strong/20 blur-3xl" />
        </div>
        <div className="container-page relative">
          <div className="grid items-center gap-12 lg:grid-cols-[1.2fr_.8fr]">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-water/30 bg-water/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-water">
                <Handshake className="h-4 w-4" /> Private Partner Opportunity
              </div>
              <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                Local water-damage enquiries. One trusted Tri-Cities partner.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/72">
                Tri-Cities Water Restoration is being developed as a focused local lead-generation asset for homeowners in Coquitlam, Port Coquitlam and Port Moody. The objective is simple: create qualified local demand and connect it with a professional restoration provider that can respond well.
              </p>
              <div className="mt-8 flex flex-wrap gap-3 text-sm text-white/75">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2"><CheckCircle2 className="h-4 w-4 text-water" /> Tri-Cities only</span>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2"><CheckCircle2 className="h-4 w-4 text-water" /> Calls + form enquiries</span>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2"><CheckCircle2 className="h-4 w-4 text-water" /> Performance reporting</span>
              </div>
              <a href="#performance" className="mt-9 inline-flex items-center gap-2 rounded-full bg-water px-6 py-3.5 text-sm font-extrabold text-navy-deep transition hover:-translate-y-0.5 hover:shadow-lg">
                Explore partner dashboard <ArrowRight className="h-4 w-4" />
              </a>
            </div>

            <div className="rounded-[28px] border border-white/15 bg-white/8 p-7 shadow-2xl backdrop-blur sm:p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-water text-navy-deep"><TrendingUp className="h-6 w-6" /></div>
              <div className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-water">Built for a long-term local relationship</div>
              <h2 className="mt-3 font-display text-2xl font-extrabold">A lead asset designed around partner value</h2>
              <p className="mt-4 text-sm leading-relaxed text-white/65">
                Rather than selling generic marketing services, the goal is to build a focused local property that generates measurable homeowner interest and can be routed to a reliable restoration company.
              </p>
              <div className="mt-7 space-y-4 border-t border-white/10 pt-6">
                <div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 text-water" /><div><div className="font-bold">Controlled lead source</div><div className="mt-1 text-sm text-white/55">The website, number and tracking remain centrally managed.</div></div></div>
                <div className="flex items-start gap-3"><MapPinned className="mt-0.5 h-5 w-5 text-water" /><div><div className="font-bold">Defined service area</div><div className="mt-1 text-sm text-white/55">Coquitlam, Port Coquitlam and Port Moody.</div></div></div>
                <div className="flex items-start gap-3"><BarChart3 className="mt-0.5 h-5 w-5 text-water" /><div><div className="font-bold">Transparent reporting</div><div className="mt-1 text-sm text-white/55">Lead activity can be reviewed instead of estimated.</div></div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="partnership" className="bg-white py-20">
        <div className="container-page">
          <div className="mx-auto max-w-3xl text-center">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-water-strong">The opportunity</div>
            <h2 className="mt-3 font-display text-3xl font-extrabold text-primary sm:text-4xl">A simpler way to add local restoration opportunities</h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              The site is built around emergency and high-intent water-damage searches. A future partner can focus on responding to homeowners and completing restoration work while the lead-generation asset focuses on discovery, capture and measurement.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-2xl border border-border bg-secondary/45 p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-water-light text-water-strong"><Icon className="h-5 w-5" /></div>
                <h3 className="mt-5 font-display text-lg font-extrabold text-primary">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary py-20">
        <div className="container-page">
          <div className="grid gap-12 lg:grid-cols-[.9fr_1.1fr] lg:items-start">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-water-strong">How it works</div>
              <h2 className="mt-3 font-display text-3xl font-extrabold text-primary sm:text-4xl">Clear from search to service</h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
                The model is intentionally straightforward. Homeowners need a fast path to local help; the partner needs useful lead information and a measurable source.
              </p>
            </div>
            <div className="space-y-4">
              {STEPS.map((step) => (
                <div key={step.n} className="grid grid-cols-[64px_1fr] gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm sm:p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy-deep font-display text-sm font-extrabold text-water">{step.n}</div>
                  <div>
                    <h3 className="font-display text-lg font-extrabold text-primary">{step.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-page">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-water-strong">Local coverage</div>
              <h2 className="mt-3 font-display text-3xl font-extrabold text-primary sm:text-4xl">Focused on the Tri-Cities, not all of Metro Vancouver</h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
                The narrow geographic focus is deliberate. It keeps the site relevant to the communities a local restoration partner can realistically serve and gives each city its own dedicated search presence.
              </p>
            </div>
            <div className="grid gap-4">
              {COVERAGE.map((area) => (
                <div key={area.city} className="flex gap-4 rounded-2xl border border-border bg-secondary/35 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-water-light text-water-strong"><MapPin className="h-5 w-5" /></span>
                  <div>
                    <h3 className="font-display text-lg font-extrabold text-primary">{area.city}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{area.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-navy-deep py-20 text-white">
        <div className="container-page">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_.95fr] lg:items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-water">Partner profile</div>
              <h2 className="mt-3 font-display text-3xl font-extrabold sm:text-4xl">Looking for the right operator, not simply any company</h2>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/65">
                Water-damage leads are time-sensitive. The strongest partnership is with a company that can answer promptly, communicate professionally and treat each homeowner enquiry as a real opportunity.
              </p>
            </div>
            <div className="rounded-3xl border border-white/15 bg-white/7 p-6 sm:p-8">
              <ul className="space-y-4">
                {PARTNER_PROFILE.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-white/75">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-water" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-page">
          <div className="mx-auto max-w-6xl overflow-hidden rounded-[32px] border border-water/25 bg-gradient-to-br from-water-light via-white to-secondary p-8 shadow-sm sm:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-water-strong shadow-sm">
                  <Clock3 className="h-4 w-4" /> 14-Day Free Partner Trial
                </div>
                <h2 className="mt-5 max-w-3xl font-display text-3xl font-extrabold text-primary sm:text-4xl">
                  Experience the partnership before making a commitment.
                </h2>
                <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground">
                  During the 14-day pilot, qualifying enquiries generated through Tri-Cities Water Restoration can be routed to your company at no cost. If the partnership delivers value, continue on a simple fixed monthly partnership.
                </p>
                <div className="mt-7 flex flex-wrap gap-3 text-sm font-semibold text-primary">
                  {['No upfront fee', 'No pay-per-lead fees', 'No long-term contract', 'Cancel anytime'].map((item) => (
                    <span key={item} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2">
                      <CheckCircle2 className="h-4 w-4 text-water-strong" /> {item}
                    </span>
                  ))}
                </div>
              </div>
              <a href="#partner-contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-navy-deep px-7 py-4 text-sm font-extrabold text-white transition hover:-translate-y-0.5 hover:shadow-lg">
                Start your 14-day trial <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-20">
        <div className="container-page">
          <div className="mx-auto max-w-3xl text-center">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-water-strong">Growth options</div>
            <h2 className="mt-3 font-display text-3xl font-extrabold text-primary sm:text-4xl">Build organic demand. Add paid growth when it makes sense.</h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Start with the lead-generation partnership and expand into managed advertising when you want to increase reach and lead volume. Advertising budgets are always separate from management services.
            </p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {GROWTH_OPTIONS.map(({ icon: Icon, eyebrow, title, text: body, features }, index) => (
              <div key={title} className={`flex h-full flex-col rounded-[26px] border p-7 shadow-sm ${index === 1 ? 'border-water/40 bg-white ring-1 ring-water/10' : 'border-border bg-white'}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-water-light text-water-strong"><Icon className="h-6 w-6" /></div>
                <div className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-water-strong">{eyebrow}</div>
                <h3 className="mt-2 font-display text-2xl font-extrabold text-primary">{title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{body}</p>
                <ul className="mt-6 flex-1 space-y-3">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-sm leading-relaxed text-primary">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-water-strong" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl rounded-[30px] border border-border bg-secondary/45 p-7 sm:p-10">
            <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-water-strong"><Clock3 className="h-4 w-4" /> Current stage</div>
                <h2 className="mt-3 font-display text-2xl font-extrabold text-primary sm:text-3xl">The asset is in its launch and validation phase</h2>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  Search visibility and lead volume are being built and measured now. No guaranteed lead volume is presented. The purpose of this page is to introduce the model and identify a strong future restoration partner as performance develops.
                </p>
              </div>
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-water-strong shadow-sm"><TrendingUp className="h-7 w-7" /></div>
            </div>
          </div>
        </div>
      </section>

      <section id="performance" className="bg-secondary py-20">
        <div className="container-page">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
              <div className="max-w-2xl">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-water-strong">Private performance access</div>
                <h2 className="mt-3 font-display text-3xl font-extrabold text-primary">Partner performance dashboard</h2>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  Approved partners can use a private access key to review live performance information when dashboard access is enabled.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold text-muted-foreground"><LockKeyhole className="h-4 w-4 text-water-strong" /> Private & noindex</div>
            </div>

            {!stats ? (
              <div className="mt-8 grid gap-6 lg:grid-cols-[.85fr_1.15fr]">
                <form onSubmit={loadStats} className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-7">
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary"><LockKeyhole className="h-4 w-4 text-water-strong" /> Partner access key</div>
                  <input
                    type="password"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    className="mt-4 w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-water"
                    placeholder="Enter access key"
                  />
                  {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
                  <button disabled={loading || !key} className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy-deep px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
                    {loading ? 'Loading…' : 'View dashboard'} <ArrowRight className="h-4 w-4" />
                  </button>
                </form>

                <div className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-7">
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-water-light text-water-strong"><BarChart3 className="h-5 w-5" /></span>
                    <div>
                      <h3 className="font-display text-lg font-extrabold text-primary">What the dashboard is designed to show</h3>
                      <p className="text-xs text-muted-foreground">Available once private reporting is enabled.</p>
                    </div>
                  </div>
                  <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    {['Form lead totals', 'Call activity', '30-day enquiries', 'Top service area', 'Recent lead activity', 'Lead status visibility'].map((item) => (
                      <div key={item} className="flex items-center gap-2 rounded-xl bg-secondary/55 px-4 py-3 text-sm font-semibold text-primary"><CheckCircle2 className="h-4 w-4 text-water-strong" /> {item}</div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {(() => {
                  const p = stats.periods?.[period] || {};
                  const phoneConnected = stats.phone_tracking_connected !== false;
                  const callValue = phoneConnected ? (p.call_clicks ?? 0) : '—';
                  const totalActions = p.verified_enquiries ?? (p.form_leads || 0);
                  return (
                    <>
                      <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-white p-4 shadow-sm">
                        <div>
                          <div className="text-xs font-bold uppercase tracking-[0.16em] text-water-strong">Reporting period</div>
                          <div className="mt-1 text-sm text-muted-foreground">Switch between monthly, annual and lifetime performance.</div>
                        </div>
                        <div className="inline-flex rounded-full border border-border bg-secondary p-1">
                          {[
                            ['month', 'This Month'],
                            ['year', 'This Year'],
                            ['all', 'All Time'],
                          ].map(([value, label]) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() => setPeriod(value)}
                              className={`rounded-full px-4 py-2 text-xs font-extrabold transition ${period === value ? 'bg-navy-deep text-white shadow-sm' : 'text-muted-foreground hover:text-primary'}`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold">
                        <span className={`rounded-full px-3 py-1.5 ${stats.airtable_connected ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>Airtable {stats.airtable_connected ? 'connected' : 'not connected'}</span>
                        <span className={`rounded-full px-3 py-1.5 ${stats.phone_stats_connected ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>Phone calls {stats.phone_stats_connected ? 'tracked' : 'not connected'}</span>
                        <span className={`rounded-full px-3 py-1.5 ${phoneConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>GA4 {phoneConnected ? 'connected' : 'not connected'}</span>
                      </div>

                      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                        <StatCard icon={Send} label="Form leads" value={p.form_leads ?? 0} sub={period === 'month' ? 'Current calendar month' : period === 'year' ? 'Current calendar year' : 'Since tracking began'} />
                        <StatCard icon={Phone} label="Phone clicks" value={callValue} sub={phoneConnected ? 'GA4 tracked call intent' : 'Connect GA4 Data API'} />
                        <StatCard icon={Phone} label="Verified calls" value={stats.phone_stats_connected ? (p.actual_calls ?? 0) : '—'} sub={stats.phone_stats_connected ? 'Verified calls recorded in Airtable' : 'Phone Stats table not connected'} />
                        <StatCard icon={MapPin} label="Top city" value={p.cities?.[0]?.city || '—'} sub={p.cities?.[0] ? `${p.cities[0].count} form leads` : 'No leads in this period'} compactValue />
                        <StatCard icon={BarChart3} label="Verified enquiries" value={totalActions} sub="Form leads + verified phone calls" />
                      </div>

                      <div className="mt-8 grid gap-6 lg:grid-cols-[1.25fr_.75fr]">
                        <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <h3 className="font-display text-lg font-bold text-primary">12-month lead trend</h3>
                              <p className="mt-1 text-xs text-muted-foreground">Form leads by calendar month.</p>
                            </div>
                            <CalendarDays className="h-5 w-5 text-water-strong" />
                          </div>
                          <div className="mt-6 grid grid-cols-6 gap-2 sm:grid-cols-12">
                            {(stats.monthly_trend || []).map((item) => {
                              const max = Math.max(1, ...(stats.monthly_trend || []).map((m) => m.form_leads || 0));
                              const height = Math.max(8, Math.round(((item.form_leads || 0) / max) * 96));
                              return (
                                <div key={item.month} className="flex flex-col items-center justify-end gap-2">
                                  <div className="text-[10px] font-bold text-primary">{item.form_leads || 0}</div>
                                  <div className="flex h-24 w-full items-end justify-center rounded-lg bg-secondary/70 px-1.5">
                                    <div className="w-full rounded-md bg-water-strong/80" style={{ height: `${height}px` }} />
                                  </div>
                                  <div className="text-[10px] font-semibold text-muted-foreground">{item.label}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div className="rounded-2xl border border-border bg-white p-6 shadow-sm">
                          <div className="flex items-center gap-3">
                            <Layers3 className="h-5 w-5 text-water-strong" />
                            <h3 className="font-display text-lg font-bold text-primary">Lead mix</h3>
                          </div>
                          <div className="mt-6 space-y-4">
                            {(p.cities || []).slice(0, 5).map((city) => (
                              <div key={city.city}>
                                <div className="flex justify-between gap-3 text-sm"><span className="font-semibold text-primary">{city.city}</span><span className="text-muted-foreground">{city.count}</span></div>
                                <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
                                  <div className="h-full rounded-full bg-water-strong" style={{ width: `${Math.min(100, Math.max(6, Math.round((city.count / Math.max(1, p.form_leads || 1)) * 100)))}%` }} />
                                </div>
                              </div>
                            ))}
                            {!p.cities?.length && <div className="text-sm text-muted-foreground">No lead data for this period yet.</div>}
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}

                <div className="mt-8 overflow-hidden rounded-2xl border border-border bg-white">
                  <div className="border-b border-border px-6 py-4">
                    <h3 className="font-display text-lg font-bold text-primary">Recent leads</h3>
                    <p className="text-xs text-muted-foreground">Latest form submissions captured by the website.</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left text-sm">
                      <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                        <tr><th className="px-5 py-3">Date</th><th className="px-5 py-3">Name</th><th className="px-5 py-3">Phone</th><th className="px-5 py-3">City</th><th className="px-5 py-3">Source</th><th className="px-5 py-3">Status</th></tr>
                      </thead>
                      <tbody>
                        {stats.recent_leads?.length ? stats.recent_leads.map((lead) => (
                          <tr key={lead.id} className="border-t border-border">
                            <td className="px-5 py-3 text-muted-foreground">{new Date(lead.created_at).toLocaleString()}</td>
                            <td className="px-5 py-3 font-semibold text-primary">{lead.name}</td>
                            <td className="px-5 py-3"><a className="text-water-strong hover:underline" href={`tel:${lead.phone}`}>{lead.phone}</a></td>
                            <td className="px-5 py-3">{lead.city}</td>
                            <td className="px-5 py-3">{lead.source}</td>
                            <td className="px-5 py-3 capitalize">{lead.status}</td>
                          </tr>
                        )) : <tr><td colSpan="6" className="px-5 py-8 text-center text-muted-foreground">No leads yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <section id="partner-contact" className="bg-white py-20">
        <div className="container-page">
          <div className="mx-auto max-w-6xl overflow-hidden rounded-[32px] bg-navy-deep text-white shadow-2xl">
            <div className="grid lg:grid-cols-[.82fr_1.18fr]">
              <div className="relative overflow-hidden border-b border-white/10 p-8 sm:p-10 lg:border-b-0 lg:border-r">
                <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-water/15 blur-3xl" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 rounded-full border border-water/25 bg-water/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-water">
                    <Handshake className="h-4 w-4" /> Partner enquiry
                  </div>
                  <h2 className="mt-5 font-display text-3xl font-extrabold leading-tight sm:text-4xl">Start a partner conversation</h2>
                  <p className="mt-5 text-sm leading-relaxed text-white/65">
                    Tell us a little about your restoration company and the areas you serve. We review partnership enquiries based on responsiveness, service quality and fit with the Tri-Cities coverage area.
                  </p>

                  <div className="mt-8 space-y-5 border-t border-white/10 pt-7">
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/8 text-water"><MapPinned className="h-5 w-5" /></span>
                      <div><div className="font-bold">Focused local coverage</div><div className="mt-1 text-sm text-white/50">Coquitlam, Port Coquitlam and Port Moody.</div></div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/8 text-water"><Gauge className="h-5 w-5" /></span>
                      <div><div className="font-bold">Performance-led relationship</div><div className="mt-1 text-sm text-white/50">The goal is measurable lead generation and transparent reporting.</div></div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/8 text-water"><ShieldCheck className="h-5 w-5" /></span>
                      <div><div className="font-bold">Selective partnership</div><div className="mt-1 text-sm text-white/50">We are looking for a reliable long-term restoration provider.</div></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 text-primary sm:p-10">
                {partnerSuccess ? (
                  <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-water-light text-water-strong"><CheckCircle2 className="h-8 w-8" /></span>
                    <h3 className="mt-5 font-display text-2xl font-extrabold">Enquiry received</h3>
                    <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">Thank you for your interest in the Tri-Cities partner opportunity. Your information has been sent for review.</p>
                    <button type="button" onClick={() => setPartnerSuccess(false)} className="mt-6 rounded-full border border-border px-5 py-2.5 text-sm font-bold text-primary transition hover:bg-secondary">Send another enquiry</button>
                  </div>
                ) : (
                  <form onSubmit={submitPartnerEnquiry} className="space-y-5">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.18em] text-water-strong">Partnership contact</div>
                      <h3 className="mt-2 font-display text-2xl font-extrabold text-primary">Tell us about your company</h3>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <label className="block">
                        <span className="mb-2 flex items-center gap-2 text-sm font-bold text-primary"><Users className="h-4 w-4 text-water-strong" /> Name</span>
                        <input required value={partnerForm.name} onChange={updatePartnerForm('name')} className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/15" placeholder="Your name" />
                      </label>
                      <label className="block">
                        <span className="mb-2 flex items-center gap-2 text-sm font-bold text-primary"><Building2 className="h-4 w-4 text-water-strong" /> Company</span>
                        <input required value={partnerForm.company} onChange={updatePartnerForm('company')} className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/15" placeholder="Company name" />
                      </label>
                      <label className="block">
                        <span className="mb-2 flex items-center gap-2 text-sm font-bold text-primary"><Phone className="h-4 w-4 text-water-strong" /> Phone number</span>
                        <input required type="tel" value={partnerForm.phone} onChange={updatePartnerForm('phone')} className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/15" placeholder="Phone number" />
                      </label>
                      <label className="block">
                        <span className="mb-2 flex items-center gap-2 text-sm font-bold text-primary"><Mail className="h-4 w-4 text-water-strong" /> Email</span>
                        <input required type="email" value={partnerForm.email} onChange={updatePartnerForm('email')} className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/15" placeholder="Email address" />
                      </label>
                    </div>

                    <label className="block">
                      <span className="mb-2 flex items-center gap-2 text-sm font-bold text-primary"><Layers3 className="h-4 w-4 text-water-strong" /> Interested in</span>
                      <select required value={partnerForm.interest} onChange={updatePartnerForm('interest')} className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/15">
                        <option value="">Select an option</option>
                        <option value="Lead Generation">Lead Generation</option>
                        <option value="Google Ads Growth">Google Ads Growth</option>
                        <option value="Google + Meta Growth">Google + Meta Growth</option>
                        <option value="Not sure yet">Not sure yet</option>
                      </select>
                    </label>

                    <label className="block">
                      <span className="mb-2 flex items-center gap-2 text-sm font-bold text-primary"><FileText className="h-4 w-4 text-water-strong" /> Message</span>
                      <textarea required rows="6" value={partnerForm.message} onChange={updatePartnerForm('message')} className="w-full resize-none rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:border-water focus:ring-2 focus:ring-water/15" placeholder="Tell us about your services, coverage area and what you are looking for in a lead partnership." />
                    </label>

                    <div className="hidden" aria-hidden="true">
                      <label>Website<input tabIndex="-1" autoComplete="off" value={partnerForm.website} onChange={updatePartnerForm('website')} /></label>
                    </div>

                    {partnerError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{partnerError}</div>}

                    <button disabled={partnerSubmitting} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-water px-6 py-3.5 text-sm font-extrabold text-navy-deep transition hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                      {partnerSubmitting ? 'Sending…' : 'Start partner conversation'} <ArrowRight className="h-4 w-4" />
                    </button>
                    <p className="text-xs leading-relaxed text-muted-foreground">Your information is used only to review and respond to this partnership enquiry.</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
